import type { ContentBlock } from '@nimbus/types'
import { uniqBy } from 'es-toolkit'
import { useCoreLogger, useRoute, useSeoSchema, useSiteUrl } from '#imports'
import { escapeHtml } from '@core/utils/contentLinks'
import { contentBlocksToHtml } from '../utils/contentBlocksToHtml'
import { createFaqAnchorRegistry } from '../utils/faqAnchorRegistry'

export interface FaqSchemaItem {
    question?: string | null
    answer?: unknown
    /** Pre-rendered answer HTML, trusted as is; takes precedence over `answer`. */
    answerHtml?: string | null
    routeHash?: string | null
    /** Replaces the rendered answer as `Answer.text`. Ignored when the item has no visible answer. */
    shortAnswer?: string | null
}

export interface UseFaqSchemaReturn {
    addFaqPage: (items: Array<FaqSchemaItem | null | undefined>, pageUrl?: string) => void
}

interface FaqQuestionNode {
    '@type': 'Question'
    'name': string
    'url'?: string
    'acceptedAnswer': { '@type': 'Answer', 'text': string }
}

interface FaqPageNode {
    '@type': 'FAQPage'
    'mainEntity': FaqQuestionNode[]
}

// Process-wide on purpose: a routeHash reused across pages only shows up across requests. Dev-only.
const anchorRegistry = createFaqAnchorRegistry()

function hasVisibleText(html: string): boolean {
    return html.replace(/<[^>]*>/g, '').trim() !== ''
}

function isFaqPage(node: unknown): node is FaqPageNode {
    return typeof node === 'object' && node !== null && (node as { '@type'?: unknown })['@type'] === 'FAQPage'
}

/**
 * Emits the page's single `FAQPage` node. Repeated calls during one request merge their
 * questions into that node, skipping questions it already holds. No-op on the client.
 */
export const useFaqSchema = (): UseFaqSchemaReturn => {
    const seoSchema = useSeoSchema()
    const siteUrl = useSiteUrl()
    const route = useRoute()
    const logger = useCoreLogger()

    // `routeHash` is the id Content/Faq.vue renders on each question, so it is the
    // only fragment that resolves. Without one, or without an absolute page url
    // (JSON-LD consumers do not reliably resolve relative ones), `url` is left off.
    function questionUrl(pageUrl?: string, routeHash?: string | null): string | undefined {
        return pageUrl && routeHash ? `${pageUrl}#${encodeURIComponent(routeHash)}` : undefined
    }

    function addFaqPage(items: Array<FaqSchemaItem | null | undefined>, pageUrl = siteUrl(route.path)) {
        if (import.meta.client) return

        const faqPage = seoSchema.state.value.find(isFaqPage)
        const emitted = new Set(faqPage?.mainEntity.map(question => question.name))
        // Relative content links need the brand origin to resolve outside the page.
        const origin = siteUrl('')
        const questions = uniqBy(
            items
                .filter((item): item is FaqSchemaItem & { question: string } => !!item?.question && !emitted.has(item.question))
                .map(item => ({ ...item, answerHtml: item.answerHtml ?? contentBlocksToHtml(Array.isArray(item.answer) ? item.answer as ContentBlock[] : null, origin) }))
                .filter(item => hasVisibleText(item.answerHtml)),
            item => item.question,
        )

        if (!questions.length) return

        const mainEntity = questions.map((item): FaqQuestionNode => {
            const url = questionUrl(pageUrl, item.routeHash)
            const shortAnswer = item.shortAnswer?.trim()

            if (import.meta.dev && url && pageUrl && item.routeHash) {
                const firstPageUrl = anchorRegistry.claim(item.routeHash, pageUrl)
                if (firstPageUrl) {
                    logger.warn(`FAQ anchor "#${item.routeHash}" is used on both ${firstPageUrl} and ${pageUrl}`)
                }
            }

            return {
                '@type': 'Question',
                'name': item.question,
                ...(url && { url }),
                'acceptedAnswer': {
                    '@type': 'Answer',
                    'text': shortAnswer ? escapeHtml(shortAnswer) : item.answerHtml,
                },
            }
        })

        if (faqPage) {
            faqPage.mainEntity.push(...mainEntity)
        }
        else {
            seoSchema.add({ '@type': 'FAQPage', mainEntity })
        }
    }

    return { addFaqPage }
}
